import { useMemo } from 'react';
import type { KdsTaskDetail } from '@ordr-as/ordr-service-types/epos';
import { resolveWaitingFor } from '../selectors';
import type { FlowGraph } from '../types';
import EmptyState from '../EmptyState';
import BoardGrid from './BoardGrid';
import TaskCard from './TaskCard';

type Props = {
  tasks: KdsTaskDetail[];
  allTasks: KdsTaskDetail[];
  stationName: string;
  graph: FlowGraph;
  stationNameById: Map<number, string>;
  nowMs: number;
  selectedGroupId: string | null;
  focusedCardId: string | null;
  pendingTaskIds: Set<number>;
  onOpenDetail: (groupId: string) => void;
  onStart: (taskId: number) => void;
  onDone: (taskId: number) => void;
};

const StationBoard = ({
  tasks,
  allTasks,
  stationName,
  graph,
  stationNameById,
  nowMs,
  selectedGroupId,
  focusedCardId,
  pendingTaskIds,
  onOpenDetail,
  onStart,
  onDone,
}: Props) => {
  const waitingForNameByTaskId = useMemo(() => {
    const names = new Map<number, string | undefined>();
    for (const task of tasks) {
      if (task.status !== 'PENDING' || task.id === undefined) continue;
      const blockingStationId = resolveWaitingFor(task, allTasks, graph);
      names.set(task.id, blockingStationId === undefined ? undefined : stationNameById.get(blockingStationId));
    }
    return names;
  }, [tasks, allTasks, graph, stationNameById]);

  if (tasks.length === 0) return <EmptyState label={stationName} />;

  return (
    <BoardGrid>
      {tasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          waitingForName={task.id !== undefined ? waitingForNameByTaskId.get(task.id) : undefined}
          nowMs={nowMs}
          selected={selectedGroupId !== null && task.groupId === selectedGroupId}
          focused={task.id !== undefined && String(task.id) === focusedCardId}
          mutating={task.id !== undefined && pendingTaskIds.has(task.id)}
          onOpen={() => task.groupId && onOpenDetail(task.groupId)}
          onStart={() => task.id !== undefined && onStart(task.id)}
          onDone={() => task.id !== undefined && onDone(task.id)}
        />
      ))}
    </BoardGrid>
  );
};

export default StationBoard;
